import React, { useMemo } from 'react';
import { Skeleton, Table } from 'antd';
import { LocationData, SalesTier } from '../../services/DashboardService';
import { BaselineData, BaselineChanges } from '../../services/baselineService';
import {
  formatCurrency,
  formatPercent,
  formatPercentWithSign,
  formatCurrencyChange,
} from '../../utils/formatters';
import { BaselineDelta } from './BaselineDelta';
import { SORTABLE_COLUMNS, GlobalSortState } from '../../constants/sortableColumns';

type LocationWithEightWeekStats = LocationData & {
  eightWeekGfsPercentValue?: number | null;
  eightWeekGfsVariance?: number | null;
};

interface TierTableProps {
  tier: SalesTier;
  columns: any[];
  isQuarterly: boolean;
  baseline: BaselineData | null;
  baselineDateRange: string;
  baselineChanges: BaselineChanges | null;
  baselineLoading: boolean;
  baselineChangeLoading: boolean;
  locations?: LocationWithEightWeekStats[];
  filteredLocationsForAverage?: LocationWithEightWeekStats[]; // Filtered locations for average row calculation
  avgEightWeekPercent?: number | null;
  // Global sort props
  globalSort?: GlobalSortState;
  onGlobalSortChange?: (sort: GlobalSortState) => void;
}

export const TierTable: React.FC<TierTableProps> = ({
  tier,
  columns,
  isQuarterly,
  baseline,
  baselineDateRange,
  baselineChanges,
  baselineLoading,
  baselineChangeLoading,
  locations,
  filteredLocationsForAverage,
  avgEightWeekPercent,
  globalSort,
  onGlobalSortChange,
}) => {
  const computeEightWeekPercent = (location: LocationData): number | null => {
    if (
      !location.eightWeekTotalSales ||
      location.eightWeekTotalSales <= 0 ||
      location.eightWeekTotalGfs === null ||
      location.eightWeekTotalGfs === undefined
    ) {
      return null;
    }
    return (location.eightWeekTotalGfs / location.eightWeekTotalSales) * 100;
  };

  const baseLocations = locations ?? tier.locations;
  const computedPercents = baseLocations.map((location) =>
    location.eightWeekGfsPercentValue !== undefined && location.eightWeekGfsPercentValue !== null
      ? location.eightWeekGfsPercentValue
      : computeEightWeekPercent(location)
  );
  
  // Calculate average 8-week percent from FILTERED locations (excluding new locations that haven't appeared in at least 8 complete 8-week ranges)
  // Use filteredLocationsForAverage if provided, otherwise fall back to avgEightWeekPercent prop, then calculate from all locations
  const derivedAvgEightWeekPercent =
    avgEightWeekPercent !== undefined
      ? avgEightWeekPercent  // Use prop if provided (should already be filtered)
      : filteredLocationsForAverage && filteredLocationsForAverage.length > 0
      ? (() => {
          // Calculate from filtered locations
          const filteredPercents = filteredLocationsForAverage
            .map((location) => {
              if (
                location.eightWeekGfsPercentValue !== undefined &&
                location.eightWeekGfsPercentValue !== null
              ) {
                return location.eightWeekGfsPercentValue;
              }
              return computeEightWeekPercent(location);
            })
            .filter((value): value is number => value !== null && !Number.isNaN(value));
          
          if (filteredPercents.length === 0) {
            return null;
          }
          return filteredPercents.reduce((sum, value) => sum + value, 0) / filteredPercents.length;
        })()
      : (() => {
          // Fallback: calculate from all locations (shouldn't happen if filtering is working)
          const validPercents = computedPercents.filter(
            (value): value is number => value !== null && !Number.isNaN(value)
          );
          if (validPercents.length === 0) {
            return null;
          }
          return validPercents.reduce((sum, value) => sum + value, 0) / validPercents.length;
        })();

  // Calculate variance from filtered average (for Prior Week GFS Var from Avg column)
  // Use filtered locations to calculate the average for variance calculation
  const filteredAvgForVariance = filteredLocationsForAverage && filteredLocationsForAverage.length > 0
    ? (() => {
        const filteredPercents = filteredLocationsForAverage
          .map((loc) => {
            if (
              loc.eightWeekGfsPercentValue !== undefined &&
              loc.eightWeekGfsPercentValue !== null
            ) {
              return loc.eightWeekGfsPercentValue;
            }
            return computeEightWeekPercent(loc);
          })
          .filter((value): value is number => value !== null && !Number.isNaN(value));
        
        if (filteredPercents.length === 0) {
          return null;
        }
        return filteredPercents.reduce((sum, value) => sum + value, 0) / filteredPercents.length;
      })()
    : derivedAvgEightWeekPercent;

  const enrichedLocations: LocationWithEightWeekStats[] = baseLocations.map((location, idx) => {
    const percent = computedPercents[idx];
    // Calculate variance from filtered average, not from all locations
    const variance =
      location.eightWeekGfsVariance !== undefined && location.eightWeekGfsVariance !== null
        ? location.eightWeekGfsVariance
        : percent !== null && filteredAvgForVariance !== null
          ? percent - filteredAvgForVariance
          : null;
    return {
      ...location,
      eightWeekGfsPercentValue: percent,
      eightWeekGfsVariance: variance,
    };
  });

  const tableData = enrichedLocations.map((loc, idx) => ({
    ...loc,
    key: `${tier.tierName}-${idx}`,
  }));

  // Sort data based on global sort state
  const sortedTableData = useMemo(() => {
    if (!globalSort?.columnKey || !globalSort?.order) {
      return tableData;
    }

    const { columnKey, order } = globalSort;
    const sortConfig = SORTABLE_COLUMNS[columnKey];
    if (!sortConfig) {
      return tableData;
    }

    const dataIndex = sortConfig.dataIndex as keyof typeof tableData[0];

    return [...tableData].sort((a, b) => {
      const aVal = a[dataIndex];
      const bVal = b[dataIndex];

      // Handle string sorting (location, group)
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const diff = aVal.localeCompare(bVal);
        return order === 'ascend' ? diff : -diff;
      }

      // Handle numeric sorting
      const aNum = (aVal as number) ?? 0;
      const bNum = (bVal as number) ?? 0;
      const diff = aNum - bNum;
      return order === 'ascend' ? diff : -diff;
    });
  }, [tableData, globalSort]);

  // Handle table sort change
  const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
    if (!onGlobalSortChange) return;

    // Handle single sorter (not array)
    if (!Array.isArray(sorter) && sorter.columnKey) {
      const newOrder = sorter.order || null;
      onGlobalSortChange({
        columnKey: newOrder ? sorter.columnKey : null,
        order: newOrder,
      });
    } else if (!sorter.order) {
      // Sort was cleared
      onGlobalSortChange({ columnKey: null, order: null });
    }
  };

  // Enhance columns with controlled sort state
  const enhancedColumns = useMemo(() => {
    return columns.map((col: any) => {
      if (!col.sorter) return col;

      // Find matching sortable column config
      const sortableConfig = Object.values(SORTABLE_COLUMNS).find(
        config => config.key === col.key || config.dataIndex === col.dataIndex
      );

      if (!sortableConfig) return col;

      return {
        ...col,
        sortOrder: globalSort?.columnKey === sortableConfig.key ? globalSort.order : null,
      };
    });
  }, [columns, globalSort]);

  const renderBaselineValue = (value: React.ReactNode) =>
    baselineLoading ? (
      <Skeleton.Input active size="small" style={{ width: '80%', maxWidth: 90 }} />
    ) : (
      value
    );

  const renderBaselineChange = (
    value: number | null | undefined,
    formatter: (val: number) => string,
    invertColor = false
  ) => {
    if (baselineLoading) {
      return null;
    }
    if (baselineChangeLoading) {
      return <Skeleton.Input active size="small" style={{ width: 60, marginTop: 2 }} />;
    }
    return <BaselineDelta changeValue={value ?? null} formatter={formatter} invertColor={invertColor} />;
  };

  return (
    <Table
      columns={enhancedColumns}
      dataSource={sortedTableData}
      pagination={false}
      size="small"
      scroll={{ x: 'max-content' }}
      onChange={handleTableChange}
      summary={(pageData) => {
        // Use filtered locations for average calculation if provided, otherwise use all tableData
        const locationsForAverage = filteredLocationsForAverage && filteredLocationsForAverage.length > 0
          ? filteredLocationsForAverage
          : tableData;
        const averageCount = locationsForAverage.length;
        
        const totalToast = locationsForAverage.reduce((sum, record) => sum + record.weeklySalesToast, 0);
        const totalCatering = locationsForAverage.reduce((sum, record) => sum + (record.weeklyCatering ?? 0), 0);
        const totalWeekly = locationsForAverage.reduce((sum, record) => sum + record.weeklyTotal, 0);
        const totalWeeklyGfs = locationsForAverage.reduce((sum, record) => sum + (record.weeklyGfs ?? 0), 0);
        const total8WeekGfs = locationsForAverage.reduce((sum, record) => sum + (record.eightWeekTotalGfs ?? 0), 0);
        const total8WeekSales = locationsForAverage.reduce((sum, record) => sum + record.eightWeekTotalSales, 0);
        
        // Calculate average weekly GFS percent (gfsPercent) from filtered locations
        // This is what the "8-Wk GFS %" column displays (it uses dataIndex: 'gfsPercent')
        const avg8WeekGfsPercent = locationsForAverage.length > 0
          ? locationsForAverage.reduce((sum, record) => sum + (record.gfsPercent ?? 0), 0) / locationsForAverage.length
          : null;
        
        // Calculate 8-week percent from filtered locations (for Prior Week GFS % column)
        // Calculate individual percentages first, then average them
        const avg8WeekPercent = (() => {
          const filteredPercents = locationsForAverage
            .map((record) => {
              if (
                !record.eightWeekTotalSales ||
                record.eightWeekTotalSales <= 0 ||
                record.eightWeekTotalGfs === null ||
                record.eightWeekTotalGfs === undefined
              ) {
                return null;
              }
              return (record.eightWeekTotalGfs / record.eightWeekTotalSales) * 100;
            })
            .filter((value): value is number => value !== null && !Number.isNaN(value));
          
          if (filteredPercents.length === 0) {
            return null;
          }
          return filteredPercents.reduce((sum, value) => sum + value, 0) / filteredPercents.length;
        })();
        
        // Calculate variance from the filtered average (should be 0 since it's variance from itself)
        // Use the same filtered locations and average that we're displaying
        const avgGfsPercentForVariance = avg8WeekGfsPercent;
        const totalVariance = locationsForAverage.reduce((sum, record) => {
          const variance = record.gfsPercent !== null && avgGfsPercentForVariance !== null
            ? record.gfsPercent - avgGfsPercentForVariance
            : 0;
          return sum + variance;
        }, 0);
        const avgVariance = averageCount > 0 ? totalVariance / averageCount : 0;
        const varianceColor = avgVariance <= -2 ? '#52c41a' : avgVariance >= 2 ? '#ff4d4f' : undefined;
        const priorVarianceValues = locationsForAverage
          .map((record) => record.eightWeekGfsVariance)
          .filter(
            (value): value is number => value !== null && value !== undefined && !Number.isNaN(value)
          );
        const avgPriorVariance =
          priorVarianceValues.length > 0
            ? priorVarianceValues.reduce((sum, value) => sum + value, 0) / priorVarianceValues.length
            : null;
        const priorVarianceColor =
          avgPriorVariance !== null
            ? avgPriorVariance <= -2
              ? '#52c41a'
              : avgPriorVariance >= 2
                ? '#ff4d4f'
                : undefined
            : undefined;
        const collectTrendAverage = (values: Array<number | null | undefined>) => {
          const filtered = values.filter(
            (value): value is number => value !== null && value !== undefined && !Number.isNaN(value)
          );
          if (filtered.length === 0) {
            return null;
          }
          return filtered.reduce((sum, value) => sum + value, 0) / filtered.length;
        };
        const avgGfsPercentTrend = collectTrendAverage(locationsForAverage.map((record) => record.gfsPercentTrend));
        const avgTotalSalesTrend = collectTrendAverage(locationsForAverage.map((record) => record.totalSalesTrend));
        const avgTotalGfsTrend = collectTrendAverage(locationsForAverage.map((record) => record.totalGfsTrend));
        const trendColor = (value: number | null, invert = false) =>
          value === null
            ? undefined
            : value > 0
              ? invert ? '#ff4d4f' : '#52c41a'
              : value < 0
                ? invert ? '#52c41a' : '#ff4d4f'
                : undefined;
        // Use the pre-calculated 8-week GFS percent if available (calculated from averaging individual percentages)
        // Otherwise fall back to calculating from totals (for backwards compatibility)
        const baselineEightWeekPercent =
          baseline?.eightWeekGfsPercent !== undefined && baseline?.eightWeekGfsPercent !== null
            ? baseline.eightWeekGfsPercent
            : baseline &&
              baseline.eightWeekTotalSales > 0 &&
              baseline.eightWeekTotalGfs !== null &&
              baseline.eightWeekTotalGfs !== undefined
            ? (baseline.eightWeekTotalGfs / baseline.eightWeekTotalSales) * 100
            : null;
        const shouldRenderBaselineRow = baselineLoading || Boolean(baseline);

        return (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}>
                <b>Average</b>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}></Table.Summary.Cell>
              {isQuarterly ? (
                <>
                  {/* Qtr Weekly Avg Toast */}
                  <Table.Summary.Cell index={2} align="right">
                    <b>{formatCurrency(averageCount > 0 ? totalToast / averageCount : 0)}</b>
                  </Table.Summary.Cell>
                  {/* Qtr Weekly Avg Cater */}
                  <Table.Summary.Cell index={3} align="right">
                    <b>{formatCurrency(averageCount > 0 ? totalCatering / averageCount : 0)}</b>
                  </Table.Summary.Cell>
                  {/* Qtr Weekly Avg Total Sales */}
                  <Table.Summary.Cell index={4} align="right">
                    <b>{formatCurrency(averageCount > 0 ? totalWeekly / averageCount : 0)}</b>
                  </Table.Summary.Cell>
                  {/* Qtr Weekly Avg Gfs Spend */}
                  <Table.Summary.Cell index={5} align="right">
                    <b>{totalWeeklyGfs > 0 && averageCount > 0 ? formatCurrency(totalWeeklyGfs / averageCount) : '--'}</b>
                  </Table.Summary.Cell>
                  {/* Qtr Weekly Avg Gfs % */}
                  <Table.Summary.Cell index={6} align="right">
                    <b>{formatPercent(tier.avgGfsPercent)}</b>
                  </Table.Summary.Cell>
                  {/* Qtr Weekly Avg Var from Avg */}
                  <Table.Summary.Cell index={7} align="right">
                    <b style={{ color: varianceColor }}>{formatPercentWithSign(avgVariance)}</b>
                  </Table.Summary.Cell>
                  {/* GFS % PRVS QTR CHG */}
                  <Table.Summary.Cell index={8} align="right">
                    <b style={{ color: trendColor(avgGfsPercentTrend, true) }}>
                      {avgGfsPercentTrend !== null ? formatPercentWithSign(avgGfsPercentTrend) : '--'}
                    </b>
                  </Table.Summary.Cell>
                  {/* Toast Sales */}
                  <Table.Summary.Cell index={9} align="right">
                    <b>
                      {locationsForAverage.reduce((sum, r) => sum + (r.quarterlyToastSales ?? 0), 0) > 0 && averageCount > 0
                        ? formatCurrency(locationsForAverage.reduce((sum, r) => sum + (r.quarterlyToastSales ?? 0), 0) / averageCount)
                        : '--'}
                    </b>
                  </Table.Summary.Cell>
                  {/* Ez Cater Sales */}
                  <Table.Summary.Cell index={10} align="right">
                    <b>
                      {locationsForAverage.reduce((sum, r) => sum + (r.quarterlyEzCaterSales ?? 0), 0) > 0 && averageCount > 0
                        ? formatCurrency(locationsForAverage.reduce((sum, r) => sum + (r.quarterlyEzCaterSales ?? 0), 0) / averageCount)
                        : '--'}
                    </b>
                  </Table.Summary.Cell>
                  {/* Total Sales */}
                  <Table.Summary.Cell index={11} align="right">
                    <b>
                      {total8WeekSales > 0 && averageCount > 0 ? (
                        formatCurrency(total8WeekSales / averageCount)
                      ) : (
                        <span style={{ color: '#ff4d4f' }}>--</span>
                      )}
                    </b>
                  </Table.Summary.Cell>
                  {/* Total Sales PRVS QTR CHG */}
                  <Table.Summary.Cell index={12} align="right">
                    <b style={{ color: trendColor(avgTotalSalesTrend) }}>
                      {avgTotalSalesTrend !== null ? formatCurrencyChange(avgTotalSalesTrend) : '--'}
                    </b>
                  </Table.Summary.Cell>
                  {/* Total GFS */}
                  <Table.Summary.Cell index={13} align="right">
                    <b>
                      {total8WeekGfs > 0 && averageCount > 0 ? (
                        formatCurrency(total8WeekGfs / averageCount)
                      ) : (
                        <span style={{ color: '#d9d9d9' }}>--</span>
                      )}
                    </b>
                  </Table.Summary.Cell>
                  {/* Total GFS PRVS QTR CHG */}
                  <Table.Summary.Cell index={14} align="right">
                    <b style={{ color: trendColor(avgTotalGfsTrend) }}>
                      {avgTotalGfsTrend !== null ? formatCurrencyChange(avgTotalGfsTrend) : '--'}
                    </b>
                  </Table.Summary.Cell>
                </>
              ) : (
                <>
                  <Table.Summary.Cell index={2} align="right">
                    <b>
                      {total8WeekSales > 0 && averageCount > 0 ? (
                        formatCurrency(total8WeekSales / averageCount)
                      ) : (
                        <span style={{ color: '#ff4d4f' }}>--</span>
                      )}
                    </b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <b>
                      {total8WeekGfs > 0 && averageCount > 0 ? (
                        formatCurrency(total8WeekGfs / averageCount)
                      ) : (
                        <span style={{ color: '#ff4d4f' }}>--</span>
                      )}
                    </b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    <b>{avg8WeekPercent !== null ? formatPercent(avg8WeekPercent) : '--'}</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align="right">
                    {avgPriorVariance !== null ? (
                      <b style={{ color: priorVarianceColor }}>{formatPercentWithSign(avgPriorVariance)}</b>
                    ) : (
                      <b>--</b>
                    )}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    <b>{formatCurrency(averageCount > 0 ? totalToast / averageCount : 0)}</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right">
                    <b>{formatCurrency(averageCount > 0 ? totalCatering / averageCount : 0)}</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8} align="right">
                    <b>{formatCurrency(averageCount > 0 ? totalWeekly / averageCount : 0)}</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9} align="right">
                    <b>{totalWeeklyGfs > 0 && averageCount > 0 ? formatCurrency(totalWeeklyGfs / averageCount) : '--'}</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={10} align="right">
                    <b>{avg8WeekGfsPercent !== null ? formatPercent(avg8WeekGfsPercent) : '--'}</b>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={11} align="right">
                    <b style={{ color: varianceColor }}>{formatPercent(avgVariance)}</b>
                  </Table.Summary.Cell>
                </>
              )}
            </Table.Summary.Row>
            {shouldRenderBaselineRow && (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <div>
                    <b>Baseline Avg.</b>
                    {baselineLoading ? (
                      <Skeleton.Input active size="small" style={{ width: 160, height: 14, marginTop: 4 }} />
                    ) : (
                      baselineDateRange && (
                        <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>({baselineDateRange})</div>
                      )
                    )}
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}></Table.Summary.Cell>
                {isQuarterly ? (
                  <>
                    {/* Qtr Weekly Avg Toast */}
                    <Table.Summary.Cell index={2} align="right">
                      <b>{renderBaselineValue(baseline ? formatCurrency(baseline.weeklyToast) : '--')}</b>
                    </Table.Summary.Cell>
                    {/* Qtr Weekly Avg Cater */}
                    <Table.Summary.Cell index={3} align="right">
                      <b>{renderBaselineValue(baseline ? formatCurrency(baseline.weeklyCatering) : '--')}</b>
                    </Table.Summary.Cell>
                    {/* Qtr Weekly Avg Total Sales */}
                    <Table.Summary.Cell index={4} align="right">
                      <div style={{ textAlign: 'right' }}>
                        <b>{renderBaselineValue(baseline ? formatCurrency(baseline.weeklyTotal) : '--')}</b>
                        {renderBaselineChange(baselineChanges?.weeklyTotalDelta ?? null, formatCurrencyChange)}
                      </div>
                    </Table.Summary.Cell>
                    {/* Qtr Weekly Avg Gfs Spend */}
                    <Table.Summary.Cell index={5} align="right">
                      <div style={{ textAlign: 'right' }}>
                        <b>
                          {renderBaselineValue(
                            baseline && baseline.weeklyGfs !== null && baseline.weeklyGfs > 0
                              ? formatCurrency(baseline.weeklyGfs)
                              : '--'
                          )}
                        </b>
                        {renderBaselineChange(baselineChanges?.weeklyGfsDelta ?? null, formatCurrencyChange)}
                      </div>
                    </Table.Summary.Cell>
                    {/* Qtr Weekly Avg Gfs % */}
                    <Table.Summary.Cell index={6} align="right">
                      <div style={{ textAlign: 'right' }}>
                        <b>{renderBaselineValue(baseline ? formatPercent(baseline.gfsPercent) : '--')}</b>
                        {renderBaselineChange(
                          baselineChanges?.gfsPercentPoints ?? null,
                          formatPercentWithSign,
                          true
                        )}
                      </div>
                    </Table.Summary.Cell>
                    {/* Qtr Weekly Avg Var from Avg */}
                    <Table.Summary.Cell index={7} align="right">
                      <b>{renderBaselineValue(baseline ? formatPercent(baseline.varianceFromAvg) : '--')}</b>
                    </Table.Summary.Cell>
                    {/* GFS % PRVS QTR CHG */}
                    <Table.Summary.Cell index={8} align="right">
                      <b>{renderBaselineValue('--')}</b>
                    </Table.Summary.Cell>
                    {/* Toast Sales */}
                    <Table.Summary.Cell index={9} align="right">
                      <b>{renderBaselineValue('--')}</b>
                    </Table.Summary.Cell>
                    {/* Ez Cater Sales */}
                    <Table.Summary.Cell index={10} align="right">
                      <b>{renderBaselineValue('--')}</b>
                    </Table.Summary.Cell>
                    {/* Total Sales */}
                    <Table.Summary.Cell index={11} align="right">
                      <b>
                        {renderBaselineValue(
                          baseline && baseline.eightWeekTotalSales > 0 ? formatCurrency(baseline.eightWeekTotalSales) : '--'
                        )}
                      </b>
                    </Table.Summary.Cell>
                    {/* Total Sales PRVS QTR CHG */}
                    <Table.Summary.Cell index={12} align="right">
                      <b>{renderBaselineValue('--')}</b>
                    </Table.Summary.Cell>
                    {/* Total GFS */}
                    <Table.Summary.Cell index={13} align="right">
                      <b>
                        {renderBaselineValue(
                          baseline && baseline.eightWeekTotalGfs > 0 ? formatCurrency(baseline.eightWeekTotalGfs) : '--'
                        )}
                      </b>
                    </Table.Summary.Cell>
                    {/* Total GFS PRVS QTR CHG */}
                    <Table.Summary.Cell index={14} align="right">
                      <b>{renderBaselineValue('--')}</b>
                    </Table.Summary.Cell>
                  </>
                ) : (
                  <>
                    <Table.Summary.Cell index={2} align="right">
                      <b>
                        {renderBaselineValue(
                          baseline && baseline.eightWeekTotalSales > 0 ? formatCurrency(baseline.eightWeekTotalSales) : '--'
                        )}
                      </b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <b>
                        {renderBaselineValue(
                          baseline && baseline.eightWeekTotalGfs > 0 ? formatCurrency(baseline.eightWeekTotalGfs) : '--'
                        )}
                      </b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      <b>
                        {renderBaselineValue(
                          baselineEightWeekPercent !== null ? formatPercent(baselineEightWeekPercent) : '--'
                        )}
                      </b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      <b>{renderBaselineValue(formatPercentWithSign(0))}</b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">
                      <b>{renderBaselineValue(baseline ? formatCurrency(baseline.weeklyToast) : '--')}</b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={7} align="right">
                      <b>{renderBaselineValue(baseline ? formatCurrency(baseline.weeklyCatering) : '--')}</b>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={8} align="right">
                      <div style={{ textAlign: 'right' }}>
                        <b>{renderBaselineValue(baseline ? formatCurrency(baseline.weeklyTotal) : '--')}</b>
                        {baseline && renderBaselineChange(baselineChanges?.weeklyTotalDelta ?? null, formatCurrencyChange)}
                      </div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={9} align="right">
                      <div style={{ textAlign: 'right' }}>
                        <b>
                          {renderBaselineValue(
                            baseline && baseline.weeklyGfs !== null && baseline.weeklyGfs > 0
                              ? formatCurrency(baseline.weeklyGfs)
                              : '--'
                          )}
                        </b>
                        {baseline && renderBaselineChange(baselineChanges?.weeklyGfsDelta ?? null, formatCurrencyChange)}
                      </div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={10} align="right">
                      <div style={{ textAlign: 'right' }}>
                        <b>{renderBaselineValue(baseline ? formatPercent(baseline.gfsPercent) : '--')}</b>
                    {baseline && renderBaselineChange(
                      baselineChanges?.gfsPercentPoints ?? null,
                      formatPercentWithSign,
                      true
                    )}
                      </div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={11} align="right">
                      <b>{renderBaselineValue(baseline ? formatPercent(baseline.varianceFromAvg) : '--')}</b>
                    </Table.Summary.Cell>
                  </>
                )}
              </Table.Summary.Row>
            )}
          </Table.Summary>
        );
      }}
    />
  );
};

